import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { CustomMoldInfo } from '../../models/CustomMoldInfo';
import {
  Alert,
  AlertService,
  AlertType,
  ModalComponent,
  ModalResult,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig
} from '@michelin/theme';
import { debounceTime, distinctUntilChanged, map, Subject, takeUntil } from 'rxjs';
import { Observable } from 'rxjs';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { Store } from '@ngxs/store';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { CustomMoldFilters } from '../../models/CustomMoldFilters';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { ProductsState } from '../../../../shared/state/ProductsState';
import { PaginatedCustomMoldInfo, PaginatedCustomMoldResponse, Pagination } from '../../models/response.model';
import { ProductDataService } from '../../services/product-data.service';
import { DealerState } from '../../../../shared/state/DealerState';
import { ExportResponse } from '../../../../shared/services/export-to-excel.service';
import { MatDialog } from '@angular/material/dialog';
import { ExportModalComponent, ExportModalData } from '../../../../shared/components/export-modal/export-modal.component';
import { ExportColumnDef } from '../../../../shared/models/export-column-def.model';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { hasRolePermission } from '../../../user-management/utils/UserSetupUtils';
import { ROLES } from '../../../authentication/authRoles.constants';
import { ColumnPreferencesService } from '../../../../shared/services/column-preferences.service';
import { ColumnPreferencesHelperService } from '../../../../shared/services/column-preferences-helper.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-custom-mold-screen',
  templateUrl: './custom-mold-screen.component.html',
  styleUrls: ['./custom-mold-screen.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomMoldScreenComponent implements OnInit, OnDestroy {
  @Input() customMoldInfo: CustomMoldInfo;
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<Element>;
  @ViewChild('discardConfirmation', { static: true }) discardModal: ModalComponent;
  @ViewChild('discardContent') discardContent: TemplateRef<any>;

  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public featureFlagsService = inject(FeatureflagsService);
  public alertService = inject(AlertService);
  public route = inject(ActivatedRoute);
  public cdr = inject(ChangeDetectorRef);
  public columnPrefService = inject(ColumnPreferencesService);
  public columnPrefsHelper = inject(ColumnPreferencesHelperService);

  pagination: Pagination;
  pageSizeOptions = [1, 5, 10, 50, 100];
  currentPage = 1;
  pageSize = 10;
  tabRouteLink: string = '/productData';
  customMoldsList: CustomMoldInfo[] = [];
  dealerName = this.store.selectSnapshot(DealerState.currentDealer).name;
  isExporting = false;
  columnNames: Record<string, string> = {};
  private dialog = inject(MatDialog);
  treads: string[];
  applications: string[];
  casingMakes: string[];
  casingSeries: string[];
  casingSizes: string[];
  axles: string[];
  treadBrands: string[];
  moldContours: string[];
  dropDownLists: string[] = [];
  hasFetchedDropDowns = false;
  filterDropdownWidth = '200px';
  displayedRowsForm: FormGroup = this.fb.group({
    rows: this.fb.array([])
  });

  // Store original data to detect changes accurately
  private originalCustomMoldsList: CustomMoldInfo[] = [];

  productSaveDisabled = true;
  saveEnabled = false;
  isModified = false;

  // visibility map for quick binding in the selector menu (default values will be applied from prefs)
  columnVisibility: Record<string, boolean> = {
    cushionSKU: false,
    treadDepth: false,
    moldContour: false,
    application: false,
    axle: false
  };

  private canonicalColumns: Record<string, any> = {};
  columnKeys: string[] = [];
  columnOrder: string[] = [];

  private ngUnsubscribe = new Subject<void>();

  tableConfig: TableConfig<CustomMoldInfo> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom',
      pageLabelOptions: {
        firstPageLabel: '',
        lastPageLabel: '',
        nextPageLabel: '',
        previousPageLabel: '',
        itemsPerPageLabel: this.translationService.translate('pageLabelOptionText')
      }
    },
    columns: {}
  };

  state = TableConfig.getState(this.tableConfig, this.route);

  formGroup = this.fb.group({
    treads: [this.state.extraFilters?.tread ?? []],
    treadBrands: [this.state.extraFilters?.treadBrand ?? []],
    casingMakes: [this.state.extraFilters?.casingMake ?? []],
    casingSeries: [this.state.extraFilters?.casingSeries ?? []],
    casingSizes: [this.state.extraFilters?.casingSizes ?? []],
    moldContours: [this.state.extraFilters?.moldContour ?? []],
    axles: [this.state.extraFilters?.axle ?? []],
    applications: [this.state.extraFilters?.application ?? []]
  });
  fetcher: PaginatorFetcher<CustomMoldInfo> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;

    const customMoldFilters: CustomMoldFilters = this.formGroup.value as CustomMoldFilters;
    return this.productDataService.getCustomMoldLines(customMoldFilters, filters.page, filters.limit).pipe(
      map((customMoldResponse: PaginatedCustomMoldResponse) => {
        this.customMoldsList = customMoldResponse.data.customMolds || [];
        this.pagination = customMoldResponse.data.pagination;
        this.updateDisplayedRowsForm(this.customMoldsList);
        return { items: this.customMoldsList, count: this.pagination.totalRecords };
      })
    );
  };
  exportDataFetcher: () => Observable<ExportResponse> = () =>
    this.productDataService.getCustomMoldLines(this.formGroup.value, 1, 10000).pipe(
      map((response: any) => ({
        data: (response.data.customMolds as CustomMoldInfo[]).map((item) => ({
          zoneTreadCompound: item.zoneTreadCompound,
          casingMake: item.casingMake,
          treadBrand: item.treadBrand,
          casingSeries: item.casingSeries,
          casingSize: item.casingSize,
          tread: item.tread,
          treadTotalWeight: item.treadTotalWeight,
          ctcWeight: item.ctcWeight,
          dealerTreadCompound: item.dealerTreadCompound,
          dealerCTC: item.dealerCTC,
          zoneCTC: item.zoneCTC,
          mixType: item.mixType,
          cushionSKU: item.cushionSKU,
          treadDepth: item.treadDepth,
          moldContour: item.moldContour,
          application: item.application,
          axle: item.axle
        }))
      }))
    );
  constructor() {}

  ngOnInit(): void {
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    this.saveEnabled = !this.productSaveDisabled && hasRolePermission(ROLES.edit_product);
    if (this.productSaveDisabled) {
      this.alertService.showAlert({
        type: AlertType.classical,
        title: this.translationService.translate('readOnlyProductDataHeader'),
        content: this.translationService.translate('readOnlyProductDataText'),
        timestamp: 60000
      } as Alert);
    }
    this.treads = this.store.selectSnapshot(ProductsState.products).allTreads;
    this.treadBrands = this.store.selectSnapshot(ProductsState.products).treadBrands;
    this.casingMakes = this.store.selectSnapshot(ProductsState.products).makes;
    this.casingSeries = this.store.selectSnapshot(ProductsState.products).series;
    this.casingSizes = this.store.selectSnapshot(ProductsState.products).sizes;
    this.applications = this.store.selectSnapshot(ProductsState.products).applications;
    this.axles = this.store.selectSnapshot(ProductsState.products).axles;
    this.moldContours = this.store.selectSnapshot(ProductsState.products).moldContours;

    this.setupFormListeners();
    this.loadInitialData();
    this.configureColumnsOnLanguageChange();
    this.store
      .select(TranslationState.currentLanguage)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        // build canonical columns (used as source of truth) and apply saved preferences
        this.canonicalColumns = {
          zoneTreadCompound: {
            cell: (elm: CustomMoldInfo) => elm.zoneTreadCompound,
            header: this.translationService.translate('zoneTreadCompoundText')
          },
          casingMake: {
            cell: (elm: CustomMoldInfo) => elm.casingMake,
            header: this.translationService.translate('casingMakesText')
          },
          treadBrand: {
            cell: (elm: CustomMoldInfo) => elm.treadBrand,
            header: this.translationService.translate('treadBrandText')
          },
          casingSeries: {
            cell: (elm: CustomMoldInfo) => elm.casingSeries,
            header: this.translationService.translate('casingSeriesText')
          },
          casingSize: {
            cell: (elm: CustomMoldInfo) => elm.casingSize,
            header: this.translationService.translate('casingSizeText')
          },
          tread: {
            cell: (elm: CustomMoldInfo) => elm.tread,
            header: this.translationService.translate('treadText')
          },
          treadTotalWeight: {
            cell: (elm: CustomMoldInfo) => elm.treadTotalWeight,
            header: this.translationService.translate('treadTotalWeightText')
          },
          ctcWeight: {
            cell: (elm: CustomMoldInfo) => elm.ctcWeight,
            header: this.translationService.translate('CTCWeightText')
          },
          dealerTreadCompound: {
            header: this.translationService.translate('dealerCompText'),
            cell: 'dealerTreadCompoundDropdown'
          },
          dealerCTC: {
            cell: 'dealerCTCDropdown',
            header: this.translationService.translate('dealerCTCText')
          },
          zoneCTC: {
            cell: (elm: CustomMoldInfo) => elm.zoneCTC,
            header: this.translationService.translate('zoneCTCText')
          },
          mixType: {
            cell: (elm: CustomMoldInfo) => elm.mixType,
            header: this.translationService.translate('mixTypeText')
          },
          cushionSKU: {
            cell: (elm: CustomMoldInfo) => elm.cushionSKU,
            header: this.translationService.translate('cushionSKUText')
          },
          treadDepth: {
            cell: (elm: CustomMoldInfo) => elm.treadDepth,
            header: this.translationService.translate('treadDepthText')
          },
          moldContour: {
            cell: (elm: CustomMoldInfo) => elm.moldContour,
            header: this.translationService.translate('moldContourText')
          },
          application: {
            cell: (elm: CustomMoldInfo) => elm.application,
            header: this.translationService.translate('applicationText')
          },
          axle: {
            cell: (elm: CustomMoldInfo) => elm.axle,
            header: this.translationService.translate('axleText')
          }
        };

        this.columnNames = {
          zoneTreadCompound: this.translationService.translate('zoneTreadCompoundText'),
          casingMake: this.translationService.translate('casingMakesText'),
          treadBrand: this.translationService.translate('treadBrandText'),
          casingSeries: this.translationService.translate('casingSeriesText'),
          casingSize: this.translationService.translate('casingSizeText'),
          tread: this.translationService.translate('treadText'),
          treadTotalWeight: this.translationService.translate('treadTotalWeightText'),
          ctcWeight: this.translationService.translate('CTCWeightText'),
          dealerTreadCompound: this.translationService.translate('dealerCompText'),
          dealerCTC: this.translationService.translate('dealerCTCText'),
          zoneCTC: this.translationService.translate('zoneCTCText'),
          mixType: this.translationService.translate('mixTypeText'),
          cushionSKU: this.translationService.translate('cushionSKUText'),
          treadDepth: this.translationService.translate('treadDepthText'),
          moldContour: this.translationService.translate('moldContourText'),
          application: this.translationService.translate('applicationText'),
          axle: this.translationService.translate('axleText')
        };

        // apply user preferences (localStorage) and initialize tableConfig.columns
        const init = this.columnPrefsHelper.initialize('custom-mold', this.canonicalColumns);
        this.tableConfig.columns = init.tableColumns;
        this.columnNames = init.columnNames;
        this.columnKeys = init.columnKeys;
        this.columnOrder = init.columnOrder;
        this.columnVisibility = init.columnVisibility;
        this.computeFilterDropdownWidth();
      });
    if (this.tableRef) {
      this.tableRef.patchExtraFilters(this.formGroup.value, false);
    }
    this.computeFilterDropdownWidth();
  }

  private computeFilterDropdownWidth(): void {
    const t = (key: string) => this.translationService.translate(key);
    const placeholders = [
      t('treadSearchText'),
      t('selectCasingMakeText'),
      t('selectSeriesText'),
      t('selectCasingSizeText'),
      t('selectTreadBrandText'),
      t('selectApplicationText'),
      t('selectAxleText'),
      t('selectMoldContoursText')
    ];
    const maxLen = placeholders.reduce((max, p) => Math.max(max, p.length), 0);
    this.filterDropdownWidth = `${Math.max(200, maxLen * 8 + 56)}px`;
    this.cdr.markForCheck();
  }

  get dropdownWidthConfig(): { width: string } {
    const maxLen = this.dropDownLists.reduce((max, val) => Math.max(max, (val ?? '').length), 0);
    return { width: `${Math.max(150, maxLen * 9 + 48)}px` };
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private setupFormListeners(): void {
    this.formGroup.valueChanges.pipe(debounceTime(1000), distinctUntilChanged(), takeUntil(this.ngUnsubscribe)).subscribe((values) => {
      if (this.tableRef) {
        this.tableRef.patchExtraFilters(values, true);
      }
    });
    this.displayedRowsForm.valueChanges.subscribe(() => {
      this.isModified = this.getModifiedRows().length > 0;
    });
  }
  private loadInitialData(): void {
    const rollPartNumbers = this.store.selectSnapshot(ProductsState.products).rollPartNumbers ?? [];

    // Avoid replacing values if it's already been set from fetcher fallback
    this.dropDownLists = Array.from(new Set([...this.dropDownLists, ...rollPartNumbers])).sort();
    this.hasFetchedDropDowns = true;
  }

  private configureColumnsOnLanguageChange(): void {
    this.tableConfig.onColumnsChange = (columns: string[]) => {
      this.columnVisibility = {
        application: columns.includes('application'),
        axle: columns.includes('axle'),
        cushionSKU: columns.includes('cushionSKU'),
        treadDepth: columns.includes('treadDepth'),
        moldContour: columns.includes('moldContour')
      };
    };
  }

  // Toggle visibility for a column and persist preferences (delegates to helper)
  toggleColumn(key: string): void {
    const result = this.columnPrefsHelper.toggleAndSave('custom-mold', key, this.canonicalColumns, this.columnOrder, this.columnVisibility);
    this.tableConfig = {
      ...this.tableConfig,
      columns: result.tableColumns
    };
    this.columnVisibility = result.columnVisibility;
    this.cdr.markForCheck();
  }

  onColumnDrop(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    moveItemInArray(this.columnOrder, event.previousIndex, event.currentIndex);

    const columns: Record<string, any> = {};
    this.columnOrder.forEach((key) => {
      if (this.columnVisibility[key]) {
        columns[key] = this.canonicalColumns[key];
      }
    });

    this.tableConfig = {
      ...this.tableConfig,
      columns
    };
    this.columnPrefsHelper.save('custom-mold', this.columnOrder, this.columnVisibility, this.canonicalColumns);
    this.cdr.markForCheck();
  }

  private createRowForm(row: CustomMoldInfo) {
    return this.fb.group({
      zoneTreadCompound: [row.zoneTreadCompound],
      casingMake: [row.casingMake],
      treadBrand: [row.treadBrand],
      casingSeries: [row.casingSeries],
      casingSize: [row.casingSize],
      tread: [row.tread],
      treadTotalWeight: [row.treadTotalWeight],
      ctcWeight: [row.ctcWeight],
      dealerTreadCompound: [row.dealerTreadCompound],
      dealerCTC: [row.dealerCTC],
      zoneCTC: [row.zoneCTC],
      mixType: [row.mixType],
      cushionSKU: [row.cushionSKU],
      treadDepth: [row.treadDepth],
      moldContour: [row.moldContour],
      application: [row.application],
      axle: [row.axle]
    });
  }
  private updateDisplayedRowsForm(rows: CustomMoldInfo[]) {
    const rowForms = rows.map((row) => this.createRowForm(row));
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
    // Store deep copy of original data for accurate change detection
    this.originalCustomMoldsList = JSON.parse(JSON.stringify(rows));
    // Mark form as pristine to reset dirty state
    this.displayedRowsForm.markAsPristine();
    this.displayedRowsForm.markAsUntouched();
    this.isModified = false;
  }
  getControl(item: CustomMoldInfo, controlName: keyof CustomMoldInfo): FormControl {
    const index = this.customMoldsList.findIndex((row) => row.customMoldDimensionPk === item.customMoldDimensionPk);
    if (index === -1) {
      return new FormControl('');
    }
    return (this.displayedRowsForm.get('rows') as FormArray).at(index).get(controlName) as FormControl;
  }
  resetFilters(): void {
    this.formGroup.reset();
    // Also reset the displayed rows form to pristine state
    this.updateDisplayedRowsForm(this.customMoldsList);
  }

  save(): void {
    if (!this.saveEnabled) {
      return;
    }
    const modifiedRows = this.getModifiedRows();
    this.handleSaveResult(modifiedRows);
  }

  handleSaveResult(modifiedRows: any[]): void {
    if (modifiedRows.length > 0) {
      const payload: PaginatedCustomMoldInfo = {
        customMolds: modifiedRows,
        pagination: this.pagination
      };
      this.productDataService.updateCustomMolds(payload).subscribe({
        next: () => {
          // Sync in-memory list with saved form values so getModifiedRows() returns empty
          const formArray = this.displayedRowsForm.get('rows') as FormArray;
          this.customMoldsList = this.customMoldsList.map((original, i) => ({
            ...original,
            ...formArray.at(i).value
          }));
          // Update original data to reflect the saved state
          this.originalCustomMoldsList = JSON.parse(JSON.stringify(this.customMoldsList));

          this.displayedRowsForm.markAsPristine();
          this.displayedRowsForm.markAsUntouched();
          this.isModified = false;

          this.alertService.showAlert({
            type: AlertType.success,
            content: this.translationService.translate('camSaveSuccessText')
          } as Alert);
        },
        error: () => {
          this.alertService.showAlert({
            type: AlertType.critical,
            content: this.translationService.translate('criticalErrorAlertText')
          } as Alert);
        }
      });
    } else {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('noModifiedText')
      } as Alert);
    }
  }

  getModifiedRows(): any[] {
    const modifiedRows = [];
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    for (let i = 0; i < this.customMoldsList.length; i++) {
      const original = this.originalCustomMoldsList[i] || { ...this.customMoldsList[i] };
      const current = { ...formArray.at(i).value };

      // Compare each property, normalizing null/undefined/empty string to the same value
      const isModified = Object.keys(current).some((key) => {
        const origVal = this.normalizeValue(original[key]);
        const currVal = this.normalizeValue(current[key]);
        return origVal !== currVal;
      });

      if (isModified) {
        modifiedRows.push({ ...formArray.at(i).value, customMoldDimensionPk: this.customMoldsList[i].customMoldDimensionPk });
      }
    }
    return modifiedRows;
  }

  /**
   * Normalize values for comparison (treat null, undefined, and empty string as equivalent)
   */
  private normalizeValue(value: any): any {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    return value;
  }

  showDiscardModal(): void {
    this.discardModal.open({
      id: 'discardConfirmation',
      title: this.translationService.translate('discardChangesText'),
      width: 550,
      content: this.discardContent,
      confirm: this.translationService.translate('okText'),
      cancel: this.translationService.translate('cancelText')
    });
  }

  onDiscardModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'discardConfirmation' && result.result) {
        // Reset the form to original state
        this.updateDisplayedRowsForm(this.customMoldsList);
        if (this.tableRef) {
          this.tableRef.resetFilters();
          this.tableRef.patchExtraFilters(this.formGroup.value, true);
        }
      }
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.getModifiedRows().length > 0) {
      $event.returnValue = true;
    }
  }

  canDeactivate(): Promise<boolean> {
    if (this.getModifiedRows().length > 0) {
      return new Promise<boolean>((resolve) => {
        this.showDiscardModal();
        const modalSubscription = this.discardModal.result.subscribe((result: ModalResult) => {
          if (result && result.id === 'discardConfirmation') {
            resolve(!!result.result);
            modalSubscription.unsubscribe();
          }
        });
      });
    }
    return Promise.resolve(true);
  }

  exportTable(): void {
    this.openExportModal();
  }

  openExportModal(): void {
    const columns: ExportColumnDef[] = Object.entries(this.columnNames).map(([key, label]) => ({ key, label, selected: true }));
    const data: ExportModalData = {
      columns,
      dataFetcher: this.exportDataFetcher,
      fileNamePrefix: this.dealerName + '-CustomMold-export',
      maxExportRows: 10000
    };
    this.dialog.open(ExportModalComponent, { data, width: '480px' });
  }
}
